import { FC, ReactElement, RefCallback, useEffect, useRef, useState } from 'react'

import { Tooltip, TooltipProps } from '../Tooltip'

interface TooltipOverflowProps {
  render: (ref: RefCallback<HTMLElement>) => ReactElement
  tooltip: string
  tooltipProps?: Omit<TooltipProps, 'content' | 'children'>
}

export const TooltipOverflow: FC<TooltipOverflowProps> = ({
  render,
  tooltip,
  tooltipProps = {},
}) => {
  const [isOverflowing, setIsOverflowing] = useState(false)

  const observerRef = useRef(
    new ResizeObserver(entries => {
      for (const entry of entries) {
        const { target: element } = entry
        setIsOverflowing(element.scrollWidth > element.clientWidth)
      }
    }),
  )

  const callbackRef = (element: HTMLElement) => {
    if (element) {
      observerRef.current.observe(element)
    } else {
      observerRef.current.disconnect()
    }
  }

  useEffect(() => {
    const currentRef = observerRef.current

    return () => {
      currentRef.disconnect()
    }
  }, [])

  if (!isOverflowing) {
    return render(callbackRef)
  }

  return (
    <Tooltip content={tooltip} {...tooltipProps}>
      {render(callbackRef)}
    </Tooltip>
  )
}
